import React, { useMemo } from 'react'
import { Text, TextProps as TextPropsOriginal } from 'rebass'
import styled, {
  DefaultTheme,
  ThemeProvider as StyledComponentsThemeProvider,
  createGlobalStyle,
  css,
} from 'styled-components/macro'

import { cssStringFromTheme } from './a_themes/cssStringFromTheme'
import { darkTheme22 } from './a_themes/a_darktheme'
import { lightTheme22 } from './a_themes/a_lighttheme'
import { Acolors as ColorsPalette, colorsDark, colorsLight } from './colors'
import { opacify } from './utils'

import { Colors, ThemeColors } from './styled'
import { useIsDarkMode } from '../state/user/hooks'

export * from './components'

type TextProps = Omit<TextPropsOriginal, 'css'>

export const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280,
}

const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1536,
  xxxl: 1920,
}

const transition = {
  duration: {
    slow: '500ms',
    medium: '250ms',
    fast: '125ms',
  },
  timing: {
    ease: 'ease',
    in: 'ease-in',
    out: 'ease-out',
    inOut: 'ease-in-out',
  },
}

const opacities = {
  hover: 0.6,
  click: 0.4,
  disabled: 0.5,
  enabled: 1,
}

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
  (accumulator, size) => {
    ;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
      @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
        ${css(a, b, c)}
      }
    `
    return accumulator
  },
  {}
) as any

const white = '#FFFFFF'
const black = '#000000'

function andyThemeColors(darkMode: boolean): ThemeColors {
  return {
    userThemeColor: darkMode ? colorsDark.userThemeColor : colorsLight.userThemeColor,

    backgroundBackdrop: darkMode ? colorsDark.backgroundBackdrop : colorsLight.backgroundBackdrop,
    backgroundSurface: darkMode ? colorsDark.backgroundSurface : colorsLight.backgroundSurface,
    backgroundModule: darkMode ? colorsDark.backgroundModule : colorsLight.backgroundModule,
    backgroundFloating: darkMode ? colorsDark.backgroundFloating : colorsLight.backgroundFloating,
    backgroundInteractive: darkMode ? colorsDark.backgroundInteractive : colorsLight.backgroundInteractive,
    backgroundOutline: darkMode ? colorsDark.backgroundOutline : colorsLight.backgroundOutline,
    backgroundScrim: darkMode ? colorsDark.backgroundScrim : colorsLight.backgroundScrim,

    textPrimary: darkMode ? colorsDark.textPrimary : colorsLight.textPrimary,
    textSecondary: darkMode ? colorsDark.textSecondary : colorsLight.textSecondary,
    textTertiary: darkMode ? colorsDark.textTertiary : colorsLight.textTertiary,

    accentAction: darkMode ? colorsDark.accentAction : colorsLight.accentAction,
    accentActive: darkMode ? colorsDark.accentActive : colorsLight.accentActive,
    accentSuccess: darkMode ? colorsDark.accentSuccess : colorsLight.accentSuccess,
    accentWarning: darkMode ? colorsDark.accentWarning : colorsLight.accentWarning,
    accentFailure: darkMode ? colorsDark.accentFailure : colorsLight.accentFailure,
    accentCritical: darkMode ? colorsDark.accentCritical : colorsLight.accentCritical,

    accentActionSoft: darkMode ? colorsDark.accentActionSoft : colorsLight.accentActionSoft,
    accentActiveSoft: darkMode ? colorsDark.accentActiveSoft : colorsLight.accentActiveSoft,
    accentSuccessSoft: darkMode ? colorsDark.accentSuccessSoft : colorsLight.accentSuccessSoft,
    accentWarningSoft: darkMode ? colorsDark.accentWarningSoft : colorsLight.accentWarningSoft,
    accentFailureSoft: darkMode ? colorsDark.accentFailureSoft : colorsLight.accentFailureSoft,

    accentTextDarkPrimary: darkMode ? colorsDark.accentTextDarkPrimary : colorsLight.accentTextDarkPrimary,
    accentTextDarkSecondary: darkMode ? colorsDark.accentTextDarkSecondary : colorsLight.accentTextDarkSecondary,
    accentTextDarkTertiary: darkMode ? colorsDark.accentTextDarkTertiary : colorsLight.accentTextDarkTertiary,

    accentTextLightPrimary: darkMode ? colorsDark.accentTextLightPrimary : colorsLight.accentTextLightPrimary,
    accentTextLightSecondary: darkMode ? colorsDark.accentTextLightSecondary : colorsLight.accentTextLightSecondary,
    accentTextLightTertiary: darkMode ? colorsDark.accentTextLightTertiary : colorsLight.accentTextLightTertiary,

    white: ColorsPalette.white,
    black: ColorsPalette.black,

    // chain colors are same for light/dark mode
    chain_1: colorsDark.chain_1,
    chain_3: colorsDark.chain_3,
    chain_4: colorsDark.chain_4,
    chain_5: colorsDark.chain_5,
    chain_10: colorsDark.chain_10,
    chain_137: colorsDark.chain_137,
    chain_42: colorsDark.chain_42,
    chain_420: colorsDark.chain_420,
    chain_42161: colorsDark.chain_42161,
    chain_421611: colorsDark.chain_421611,
    chain_80001: colorsDark.chain_80001,

    shallowShadow: darkMode ? colorsDark.shallowShadow : colorsLight.shallowShadow,
    deepShadow: darkMode ? colorsDark.deepShadow : colorsLight.deepShadow,
    hoverState: opacify(24, ColorsPalette.blue200),
    hoverDefault: opacify(8, ColorsPalette.gray200),
    stateOverlayHover: darkMode ? colorsDark.stateOverlayHover : colorsLight.stateOverlayHover,
    stateOverlayPressed: darkMode ? colorsDark.stateOverlayPressed : colorsLight.stateOverlayPressed,
  }
}

function KibaColors(darkMode: boolean): Colors {
  return {
    darkMode,
    // base
    white,
    black,

    // text
    text1: darkMode ? '#fff' : '#0e121c',
    text2: darkMode ? 'rgba(255,255,255, 0.8)' : 'rgba(0,0,0, 0.8)',
    text3: darkMode ? '#fff' : '#6E727D',
    text4: darkMode ? '#fff' : '#18181E',
    text5: darkMode ? '#2C2F36' : '#EDEEF2',
    textPrimary: darkMode ? '#fff' : '#18181E',
    textSecondary: darkMode ? '#66696E' : '#565A69',
    textTertiary: darkMode ? '#66696E' : '#5E6887',

    // backgrounds / greys
    bg0: darkMode ? '#361A40' : '#FFFFFF',
    bg1: darkMode ? '#361A40' : '#FFFFFF',
    bg2: darkMode ? '#F76C1D' : '#eee',
    bg3: darkMode ? '#40444F' : '#fff',
    bg4: darkMode ? '#848484' : '#fff',
    bg5: darkMode ? '#4F4F62' : '#fff',
    bg6: darkMode ? '#272628' : 'rgba(255,255,255,1)',
    bg7: darkMode ? '#1D1D1F' : 'linear-gradient(-45deg, #B9CEF7, #F6FAF9)',
    bg8: darkMode ? '#302F32' : '#eee',
    bg9: darkMode ? 'rgba(255,255,255, 0.08)' : '#FFF',
    bg10: darkMode ? '#1F0029' : '#F9F9F9',
    bg11: darkMode ? '#2E1435' : '#F9F9F9',
    bgSwapHeader: darkMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.08)',
    bgmenu: darkMode ? '#F76C1D' : '#fff',
    backgroundModule: darkMode ? '#141B2B' : '#F5F6FC',
    backgroundSurface: darkMode ? '#361A40' : '#F9F9FB',
    buttonBlack: darkMode ? '#0F0F0F' : '#f5f5f5',

    //specialty colors
    modalBG: darkMode ? 'rgba(0,0,0,.425)' : 'rgba(0,0,0,0.3)',
    andyBG: darkMode ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,0.1)',
    andyBG2: darkMode ? '#302F32' : 'rgba(255, 255, 255, 0.9)',
    andyBG3: darkMode ? '#302F32' : 'linear-gradient(-45deg, rgba(15, 51, 158, 0.8),rgba(48, 160, 85, 0.8))',
    advancedBG: darkMode ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.6)',
    accentActive: darkMode ? '#4C82FB' : '#4C82FB',
    accentAction: darkMode ? '#4C82FB' : '#FB118E',
    accentFailure: darkMode ? '#FD766B' : '#FD766B',
    accentSuccess: darkMode ? '#00BD13' : '#00BD13',
    accentBad: darkMode ? '#FF0000' : '#FF0000',
    hoverState: opacify(24, '#ADBCFF'),

    //primary colors
    primary1: darkMode ? '#ea3627' : '#73b588',
    primary2: darkMode ? '#222' : '#c11d20',
    primary3: darkMode ? '#eee' : '#d9dc51',
    primary4: darkMode ? 'rgb(170, 64, 88)' : '#F6DDE8',
    primary5: darkMode ? '#FFF' : '#5288F4',
    primary6: darkMode
      ? 'linear-gradient(-45deg, #FF5C01, #FF0012, #FF005D)'
      : 'linear-gradient(-45deg, #FF5C01, #FF0012, #FF005D)',
    primary7: darkMode
      ? 'linear-gradient(-45deg, #FFD5C0, #FFBFC4, #FFBFD5)'
      : 'linear-gradient(-45deg, #FFD5C0, #FFBFC4, #FFBFD5)',
    primary8: darkMode
      ? 'linear-gradient(-45deg, #CC6734, #CC3340, #CC3368)'
      : 'linear-gradient(-45deg, #CC6734, #CC3340, #CC3368)',

    // color text
    primaryText1: darkMode ? '#252632' : '#FFFFFF',

    // secondary colors
    secondary1: darkMode ? '#2172E5' : '#00203d',
    secondary2: darkMode ? '#17000b26' : '#F6DDE8',
    secondary3: darkMode ? '#17000b26' : '#5288F4',

    // other
    red1: darkMode ? '#FF4343' : '#DA2D2B',
    red2: darkMode ? '#971B1C' : '#DF1F38',
    red3: '#D60000',
    green1: darkMode ? '#27AE60' : '#007D35',
    yellow1: '#E3A507',
    yellow2: '#FF8F00',
    yellow3: '#F3B71E',
    blue1: darkMode ? '#252632' : '#0068FC',
    blue2: darkMode ? '#5199FF' : '#0068FC',
    error: darkMode ? '#FD4040' : '#DF1F38',
    success: darkMode ? '#779681' : '#779681',
    warning: '#971B1C',

    // dont wanna forget these blue yet

    // uncomment below to make the background change on select of tokens
    //blue4: darkMode ? 'linear-gradient(to right, var(--color-output), var(--color-input))' : 'linear-gradient(-45deg, #B9CEF7, #F6FAF9)',
    blue4: darkMode ? 'linear-gradient(-45deg, #8D95AB, #0B0D12)' : 'linear-gradient(-45deg, #B9CEF7, #F6FAF9)',

    // blue5: darkMode ? '#153d6f70' : '#EBF4FF',

    // interactive
    backgroundInteractive: darkMode ? '#293249' : '#E8ECFB',
  }
}

function getSettings() {
  return {
    // media queries

    navHeight: 72,
    mobileBottomBarHeight: 52,

    // deprecated - please use hardcoded exported values instead of
    // adding to the theme object
    breakpoint: BREAKPOINTS,
    transition: transition,
    opacity: opacities,
  }
}

function theme(darkMode: boolean): DefaultTheme {
  return {
    ...andyThemeColors(darkMode),
    ...KibaColors(darkMode),
    ...getSettings(),

    grids: {
      sm: 8,
      md: 12,
      lg: 24,
    },

    //shadows
    shadow1: darkMode ? '#000' : '#2F80ED',
    shadow2: '4px 4px 10px rgba(0, 0, 0, 0.24), 2px 2px 4px rgba(0, 0, 0, 0.12), 1px 2px 2px rgba(0, 0, 0, 0.12);',
    shadowD: darkMode ? '' : '0px 0px 40px rgb(0 0 0 / 5%)',
    shadow4: darkMode ? '' : '',

    chartTableBg: darkMode ? '#361A40' : 'linear-gradient(white, rgb(255 255 255))',
    chartSidebar: darkMode ? '#361A40' : 'linear-gradient(#f5f5f5,rgb(255 255 255))',

    chartBgLight: '#f5f5f5',
    chartBgDark: 'bg0',
    // media queries
    mediaWidth: mediaWidthTemplates,

    // css snippets
    flexColumnNoWrap: css`
      display: flex;
      flex-flow: column nowrap;
    `,
    flexRowNoWrap: css`
      display: flex;
      flex-flow: row nowrap;
    `,
  }
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const darkMode = useIsDarkMode()

  const themeObject = useMemo(() => theme(darkMode), [darkMode])

  return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>
}

const TextWrapper = styled(Text)<{ link?: boolean; font?: string; color: keyof Colors }>`
  color: ${(props) => (props as any).theme[props?.color]};
  font-family: ${(props) => (props.font ? props.font : 'Poppins')};
  ${(props) =>
    Boolean(props.link) &&
    `&:hover {
    color: ${props.theme.blue2};
    transition: all ease in 0.1s;
  }`}
`

export const TYPE = {
  main(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text2'} {...props} />
  },
  link(props: TextProps) {
    return <TextWrapper style={{ cursor: 'pointer' }} link={true} fontWeight={500} color={'primary1'} {...props} />
  },
  label(props: TextProps) {
    return <TextWrapper fontWeight={600} color={'text1'} {...props} />
  },
  black(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'textSecondary'} {...props} />
  },
  white(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'white'} {...props} />
  },
  body(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={16} color={'text1'} {...props} />
  },
  currencyInput(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={18} color={'text1'} font={'Poppins'} {...props} />
  },
  currencyInputSub(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={12} color={'text1'} opacity={'50%'} font={'Poppins'} {...props} />
  },
  Caption(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={12} color="textPrimary" {...props} />
  },
  largeHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} {...props} />
  },
  modalHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} height={38} {...props} />
  },
  modalBody(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={14} color={'text1'} opacity={'60%'} {...props} />
  },
  modalSubheader(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={14} {...props} />
  },
  mediumHeader(props: TextProps) {
    return <TextWrapper font={'"Poppins";'} fontWeight={500} fontSize={20} {...props} />
  },
  subHeader(props: TextProps) {
    return <TextWrapper fontWeight={400} color={'text1'} fontSize={14} {...props} />
  },
  SubHeaderSmall(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={14} color="textSecondary" {...props} />
  },
  small(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={11} {...props} />
  },
  blue(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'blue1'} {...props} />
  },
  yellow(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'yellow3'} {...props} />
  },
  darkGray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text3'} {...props} />
  },
  gray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'bg3'} {...props} />
  },
  italic(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={12} fontStyle={'italic'} color={'text2'} {...props} />
  },
  error({ error, ...props }: { error: boolean } & TextProps) {
    return <TextWrapper fontWeight={500} color={error ? 'red1' : 'text2'} {...props} />
  },
}

export const ThemedGlobalStyle = createGlobalStyle`
html {
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg1} !important;
}

a {
 color: ${({ theme }) => theme.blue1}; 
}

:root {
  ${({ theme }) => (theme.darkMode ? cssStringFromTheme(darkTheme22) : cssStringFromTheme(lightTheme22))}
}
`
